import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, ChevronRight, ChevronLeft, Pizza, Check,
  ShoppingCart, Users, BarChart3, Bell,
  Plus, CreditCard, AlertTriangle, FileDown,
  Upload, Receipt, Truck, Tag, ClipboardList, SprayCan,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { Trans, useTranslation } from "react-i18next";

const STORAGE_PREFIX = "chizy_onboarding_dismissed_";

/* --- VISUAL ILLUSTRATIONS (inline SVG mockups of real screens) --- */
const Illu = {
  Dashboard: () => (
    <svg viewBox="0 0 320 180" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="320" height="180" rx="14" fill="#FFFBE6"/>
      <rect x="12" y="12" width="92" height="44" rx="8" fill="#FFDF00"/>
      <rect x="20" y="22" width="46" height="6" rx="3" fill="#1A1A1A" opacity="0.85"/>
      <rect x="20" y="34" width="32" height="14" rx="3" fill="#1A1A1A"/>
      <rect x="114" y="12" width="92" height="44" rx="8" fill="#fff" stroke="#1A1A1A" strokeWidth="1.2"/>
      <rect x="122" y="22" width="46" height="6" rx="3" fill="#1A1A1A" opacity="0.55"/>
      <rect x="122" y="34" width="32" height="14" rx="3" fill="#1A1A1A"/>
      <rect x="216" y="12" width="92" height="44" rx="8" fill="#fff" stroke="#1A1A1A" strokeWidth="1.2"/>
      <rect x="224" y="22" width="46" height="6" rx="3" fill="#1A1A1A" opacity="0.55"/>
      <rect x="224" y="34" width="32" height="14" rx="3" fill="#1A1A1A"/>
      <rect x="12" y="68" width="296" height="100" rx="10" fill="#fff" stroke="#E2E8F0" strokeWidth="1.2"/>
      <rect x="22" y="80" width="80" height="6" rx="3" fill="#1A1A1A"/>
      <rect x="22" y="96" width="276" height="3" rx="1.5" fill="#E2E8F0"/>
      {/* Bar chart */}
      <rect x="40" y="135" width="14" height="22" rx="2" fill="#FFDF00"/>
      <rect x="64" y="120" width="14" height="37" rx="2" fill="#FFDF00"/>
      <rect x="88" y="128" width="14" height="29" rx="2" fill="#FFDF00"/>
      <rect x="112" y="110" width="14" height="47" rx="2" fill="#1A1A1A"/>
      <rect x="136" y="125" width="14" height="32" rx="2" fill="#FFDF00"/>
      <rect x="160" y="118" width="14" height="39" rx="2" fill="#FFDF00"/>
      <rect x="184" y="128" width="14" height="29" rx="2" fill="#FFDF00"/>
    </svg>
  ),
  ApprovalFlow: () => (
    <svg viewBox="0 0 320 180" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="320" height="180" rx="14" fill="#FFFBE6"/>
      {/* Card list */}
      <rect x="14" y="18" width="292" height="36" rx="8" fill="#fff" stroke="#FFDF00" strokeWidth="1.5"/>
      <circle cx="32" cy="36" r="8" fill="#FFDF00"/>
      <text x="48" y="32" fontSize="9" fontWeight="700" fill="#1A1A1A">OC-2026-0007</text>
      <text x="48" y="44" fontSize="8" fill="#64748B">Lácteos Norte · $24,389</text>
      <rect x="218" y="26" width="34" height="18" rx="9" fill="#FEF3C7"/>
      <text x="222" y="38" fontSize="7" fill="#92400E" fontWeight="700">PENDIENTE</text>
      <rect x="258" y="26" width="40" height="18" rx="6" fill="#10B981"/>
      <text x="268" y="38" fontSize="7" fill="#fff" fontWeight="700">APROBAR</text>

      <rect x="14" y="60" width="292" height="36" rx="8" fill="#fff" stroke="#E2E8F0" strokeWidth="1"/>
      <circle cx="32" cy="78" r="8" fill="#E5E7EB"/>
      <text x="48" y="74" fontSize="9" fontWeight="700" fill="#1A1A1A">OC-2026-0006</text>
      <text x="48" y="86" fontSize="8" fill="#64748B">Embalajes MTY · $9,212</text>

      <rect x="14" y="102" width="292" height="36" rx="8" fill="#fff" stroke="#E2E8F0" strokeWidth="1"/>
      <circle cx="32" cy="120" r="8" fill="#E5E7EB"/>
      <text x="48" y="116" fontSize="9" fontWeight="700" fill="#1A1A1A">OC-2026-0005</text>
      <text x="48" y="128" fontSize="8" fill="#64748B">Harinas Mexicanas · $5,580</text>

      <rect x="14" y="144" width="292" height="20" rx="6" fill="#1A1A1A"/>
      <text x="28" y="158" fontSize="8" fill="#FFDF00" fontWeight="700">Validar facturas CFDI con un clic →</text>
    </svg>
  ),
  Suppliers: () => (
    <svg viewBox="0 0 320 180" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="320" height="180" rx="14" fill="#FFFBE6"/>
      <rect x="14" y="14" width="180" height="22" rx="6" fill="#fff" stroke="#E2E8F0"/>
      <circle cx="26" cy="25" r="4" fill="#94A3B8"/>
      <text x="36" y="29" fontSize="8" fill="#94A3B8">Buscar proveedor…</text>

      {[0,1,2,3].map(i => (
        <g key={i} transform={`translate(0,${44 + i*32})`}>
          <rect x="14" y="0" width="292" height="26" rx="6" fill="#fff" stroke="#E2E8F0"/>
          <circle cx="28" cy="13" r="7" fill={i === 3 ? "#FEF3C7" : "#10B98120"}/>
          <text x="40" y="12" fontSize="8.5" fontWeight="700" fill="#1A1A1A">{["Lácteos Norte","Distribuidora Pacífico","Embalajes MTY","Limpieza Norte"][i]}</text>
          <text x="40" y="22" fontSize="7.5" fill="#64748B">{["15 días crédito","21 días","15 días","21 días · pendiente"][i]}</text>
          <rect x="240" y="6" width="58" height="14" rx="7" fill={i === 3 ? "#FEF3C7" : "#DCFCE7"}/>
          <text x="248" y="16" fontSize="7" fontWeight="700" fill={i === 3 ? "#92400E" : "#065F46"}>{i === 3 ? "PENDIENTE" : "APROBADO"}</text>
        </g>
      ))}
    </svg>
  ),
  Reports: () => (
    <svg viewBox="0 0 320 180" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="320" height="180" rx="14" fill="#FFFBE6"/>
      <text x="20" y="28" fontSize="10" fontWeight="800" fill="#1A1A1A">Gasto por sucursal · Feb 2026</text>
      {/* Bell icon */}
      <circle cx="296" cy="22" r="12" fill="#1A1A1A"/>
      <text x="290" y="26" fontSize="11" fill="#FFDF00" fontWeight="900">!</text>
      <text x="296" y="16" fontSize="6" fill="#fff" fontWeight="800"></text>

      <line x1="20" y1="140" x2="300" y2="140" stroke="#E2E8F0" strokeWidth="1"/>
      <line x1="20" y1="100" x2="300" y2="100" stroke="#E2E8F0" strokeWidth="1" strokeDasharray="2 2"/>
      <line x1="20" y1="60" x2="300" y2="60" stroke="#E2E8F0" strokeWidth="1" strokeDasharray="2 2"/>

      <rect x="40" y="80" width="40" height="60" rx="4" fill="#FFDF00"/>
      <rect x="100" y="64" width="40" height="76" rx="4" fill="#FFDF00"/>
      <rect x="160" y="92" width="40" height="48" rx="4" fill="#FFDF00"/>
      <rect x="220" y="74" width="40" height="66" rx="4" fill="#1A1A1A"/>

      <text x="42" y="156" fontSize="7" fill="#64748B">HMO</text>
      <text x="100" y="156" fontSize="7" fill="#64748B">PVR</text>
      <text x="160" y="156" fontSize="7" fill="#64748B">NVR</text>
      <text x="222" y="156" fontSize="7" fill="#64748B">XAL</text>

      <rect x="60" y="168" width="200" height="10" rx="5" fill="#1A1A1A"/>
      <text x="106" y="176" fontSize="7" fill="#FFDF00" fontWeight="700">Exportar reporte mensual</text>
    </svg>
  ),
  CreatePO: () => (
    <svg viewBox="0 0 320 180" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="320" height="180" rx="14" fill="#FFFBE6"/>
      <text x="20" y="26" fontSize="10" fontWeight="800" fill="#1A1A1A">Crear nueva OC</text>
      <rect x="18" y="36" width="284" height="26" rx="6" fill="#fff" stroke="#1A1A1A"/>
      <text x="28" y="53" fontSize="9" fill="#1A1A1A">Proveedor: Lácteos del Norte ▾</text>
      {/* Items */}
      <g transform="translate(0,72)">
        <rect x="18" y="0" width="284" height="22" rx="5" fill="#fff" stroke="#E2E8F0"/>
        <text x="26" y="14" fontSize="8" fill="#1A1A1A" fontWeight="700">Queso mozzarella</text>
        <text x="160" y="14" fontSize="7" fill="#64748B">Stock: 48 kg</text>
        <rect x="220" y="4" width="32" height="14" rx="3" fill="#FFDF00"/>
        <text x="226" y="14" fontSize="7" fontWeight="800" fill="#1A1A1A">50 kg</text>
        <text x="262" y="14" fontSize="8" fontWeight="700" fill="#1A1A1A">$7,250</text>
      </g>
      <g transform="translate(0,100)">
        <rect x="18" y="0" width="284" height="22" rx="5" fill="#FEF2F2" stroke="#FECACA"/>
        <text x="26" y="14" fontSize="8" fill="#991B1B" fontWeight="700">Pepperoni</text>
        <text x="160" y="14" fontSize="7" fill="#DC2626" fontWeight="700">Stock: 4 kg ⚠</text>
        <rect x="220" y="4" width="32" height="14" rx="3" fill="#FEF2F2" stroke="#FECACA"/>
        <text x="226" y="14" fontSize="7" fontWeight="800" fill="#991B1B">20 kg</text>
        <text x="262" y="14" fontSize="8" fontWeight="700" fill="#1A1A1A">$5,700</text>
      </g>
      <rect x="18" y="132" width="200" height="30" rx="8" fill="#1A1A1A"/>
      <text x="62" y="151" fontSize="9" fontWeight="800" fill="#FFDF00">Enviar a aprobación →</text>
      <text x="236" y="151" fontSize="11" fontWeight="800" fill="#1A1A1A">$12,950</text>
    </svg>
  ),
  Receptions: () => (
    <svg viewBox="0 0 320 180" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="320" height="180" rx="14" fill="#FFFBE6"/>
      <text x="20" y="24" fontSize="10" fontWeight="800" fill="#1A1A1A">Recibir mercancía · OC-2026-0001</text>
      <rect x="18" y="34" width="284" height="32" rx="6" fill="#fff" stroke="#FFDF00"/>
      <text x="28" y="50" fontSize="9" fontWeight="700" fill="#1A1A1A">Queso mozzarella</text>
      <text x="28" y="60" fontSize="7" fill="#64748B">Pedido: 50 kg</text>
      <rect x="180" y="40" width="50" height="20" rx="4" fill="#fff" stroke="#1A1A1A"/>
      <text x="195" y="54" fontSize="9" fontWeight="700" fill="#1A1A1A">50 kg</text>
      <rect x="238" y="42" width="56" height="16" rx="8" fill="#DCFCE7"/>
      <text x="252" y="54" fontSize="7" fontWeight="800" fill="#065F46">EXACTO</text>

      <rect x="18" y="74" width="284" height="32" rx="6" fill="#fff" stroke="#F59E0B"/>
      <text x="28" y="90" fontSize="9" fontWeight="700" fill="#1A1A1A">Pepperoni</text>
      <text x="28" y="100" fontSize="7" fill="#64748B">Pedido: 30 kg</text>
      <rect x="180" y="80" width="50" height="20" rx="4" fill="#fff" stroke="#F59E0B"/>
      <text x="195" y="94" fontSize="9" fontWeight="700" fill="#92400E">28 kg</text>
      <rect x="238" y="82" width="56" height="16" rx="8" fill="#FEF3C7"/>
      <text x="248" y="94" fontSize="7" fontWeight="800" fill="#92400E">-2 kg ⚠</text>

      <rect x="18" y="116" width="284" height="22" rx="5" fill="#FEF3C7" stroke="#FCD34D"/>
      <text x="26" y="130" fontSize="7.5" fill="#92400E">Hay diferencias respecto a la OC → se avisa a corporativo automáticamente</text>

      <rect x="18" y="146" width="284" height="22" rx="6" fill="#FFDF00"/>
      <text x="92" y="160" fontSize="9" fontWeight="800" fill="#1A1A1A">Confirmar recepción →</text>
    </svg>
  ),
  Payments: () => (
    <svg viewBox="0 0 320 180" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="320" height="180" rx="14" fill="#FFFBE6"/>
      <text x="20" y="22" fontSize="10" fontWeight="800" fill="#1A1A1A">Pagos · Esta semana</text>
      {[
        { color: "#EF4444", label: "OC-0008 · Vence HOY", amount: "$31,420" },
        { color: "#F59E0B", label: "OC-0004 · Vence en 3 días", amount: "$14,850" },
        { color: "#10B981", label: "OC-0007 · Vence en 12 días", amount: "$9,250" },
      ].map((p, i) => (
        <g key={i} transform={`translate(0,${36 + i*36})`}>
          <rect x="14" y="0" width="292" height="28" rx="6" fill="#fff" stroke="#E2E8F0"/>
          <circle cx="28" cy="14" r="6" fill={p.color}/>
          <text x="42" y="13" fontSize="9" fontWeight="700" fill="#1A1A1A">{p.label}</text>
          <text x="42" y="23" fontSize="7" fill="#64748B">Transferencia · BBVA México</text>
          <text x="222" y="18" fontSize="10" fontWeight="800" fill="#1A1A1A">{p.amount}</text>
          <rect x="266" y="6" width="30" height="16" rx="3" fill={p.color}/>
          <text x="270" y="17" fontSize="6.5" fontWeight="700" fill="#fff">{i === 0 ? "PAGAR" : "PAGAR"}</text>
        </g>
      ))}
      <rect x="14" y="150" width="292" height="20" rx="6" fill="#1A1A1A"/>
      <text x="100" y="164" fontSize="8" fontWeight="800" fill="#FFDF00">⤓ Exportar CSV para banca</text>
    </svg>
  ),
  Alerts: () => (
    <svg viewBox="0 0 320 180" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="320" height="180" rx="14" fill="#FFFBE6"/>
      <rect x="14" y="14" width="292" height="44" rx="8" fill="#FEE2E2" stroke="#EF4444"/>
      <circle cx="34" cy="36" r="10" fill="#EF4444"/>
      <text x="30" y="40" fontSize="11" fill="#fff" fontWeight="800">!</text>
      <text x="54" y="32" fontSize="9" fontWeight="800" fill="#991B1B">CRÍTICO · Factura vence en 2 días</text>
      <text x="54" y="46" fontSize="7" fill="#7F1D1D">OC-2026-0008 · Distribuidora Pacífico · $24,389</text>

      <rect x="14" y="64" width="292" height="44" rx="8" fill="#FEF3C7" stroke="#F59E0B"/>
      <circle cx="34" cy="86" r="10" fill="#F59E0B"/>
      <text x="29" y="90" fontSize="11" fill="#fff" fontWeight="800">!</text>
      <text x="54" y="82" fontSize="9" fontWeight="800" fill="#92400E">ATENCIÓN · Recepción pendiente</text>
      <text x="54" y="96" fontSize="7" fill="#7C2D12">OC-2026-0009 aprobada hace 6 días sin recibir</text>

      <rect x="14" y="114" width="292" height="44" rx="8" fill="#DBEAFE" stroke="#3B82F6"/>
      <circle cx="34" cy="136" r="10" fill="#3B82F6"/>
      <text x="31" y="140" fontSize="9" fill="#fff" fontWeight="800">i</text>
      <text x="54" y="132" fontSize="9" fontWeight="800" fill="#1E3A8A">INFO · Nueva OC recibida</text>
      <text x="54" y="146" fontSize="7" fill="#1E40AF">OC-2026-0010 de Hermosillo esperando aprobación</text>
    </svg>
  ),
  Invoice: () => (
    <svg viewBox="0 0 320 180" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="320" height="180" rx="14" fill="#FFFBE6"/>
      <text x="20" y="22" fontSize="10" fontWeight="800" fill="#1A1A1A">Subir factura CFDI</text>

      <rect x="20" y="34" width="135" height="80" rx="10" fill="#fff" stroke="#E2E8F0" strokeDasharray="3 3"/>
      <text x="58" y="74" fontSize="18" fill="#FFDF00" fontWeight="800">PDF</text>
      <text x="44" y="92" fontSize="7" fill="#64748B">Arrastra el archivo aquí</text>

      <rect x="165" y="34" width="135" height="80" rx="10" fill="#fff" stroke="#E2E8F0" strokeDasharray="3 3"/>
      <text x="206" y="74" fontSize="18" fill="#FFDF00" fontWeight="800">XML</text>
      <text x="190" y="92" fontSize="7" fill="#64748B">Arrastra el archivo aquí</text>

      <rect x="20" y="124" width="280" height="20" rx="5" fill="#DCFCE7"/>
      <text x="32" y="138" fontSize="8" fontWeight="700" fill="#065F46">UUID detectado · Total coincide con la OC ($24,389)</text>

      <rect x="20" y="150" width="280" height="20" rx="6" fill="#1A1A1A"/>
      <text x="116" y="164" fontSize="9" fontWeight="800" fill="#FFDF00">Subir factura →</text>
    </svg>
  ),
  Receipts: () => (
    <svg viewBox="0 0 320 180" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="320" height="180" rx="14" fill="#FFFBE6"/>
      <text x="20" y="22" fontSize="10" fontWeight="800" fill="#1A1A1A">Comprobantes de pago</text>
      {[
        { fol: "F-20260001", date: "12/02/2026", amount: "$24,389" },
        { fol: "F-20260002", date: "08/02/2026", amount: "$9,212" },
        { fol: "F-20260003", date: "02/02/2026", amount: "$15,840" },
      ].map((p, i) => (
        <g key={i} transform={`translate(0, ${36 + i*38})`}>
          <rect x="14" y="0" width="292" height="30" rx="6" fill="#fff" stroke="#E2E8F0"/>
          <rect x="22" y="6" width="18" height="18" rx="3" fill="#10B98120"/>
          <text x="26" y="20" fontSize="11" fill="#10B981" fontWeight="800">$</text>
          <text x="48" y="14" fontSize="9" fontWeight="700" fill="#1A1A1A">{p.fol}</text>
          <text x="48" y="24" fontSize="7" fill="#64748B">Pagado el {p.date}</text>
          <text x="216" y="20" fontSize="10" fontWeight="800" fill="#1A1A1A">{p.amount}</text>
          <rect x="262" y="8" width="36" height="14" rx="4" fill="#1A1A1A"/>
          <text x="266" y="18" fontSize="7" fontWeight="800" fill="#FFDF00">⤓ PDF</text>
        </g>
      ))}
    </svg>
  ),
  PriceChanges: () => (
    <svg viewBox="0 0 320 180" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="320" height="180" rx="14" fill="#FFFBE6"/>
      <text x="20" y="22" fontSize="10" fontWeight="800" fill="#1A1A1A">Proponer cambio de precio</text>
      <rect x="18" y="34" width="135" height="64" rx="10" fill="#fff" stroke="#E2E8F0"/>
      <text x="28" y="50" fontSize="7" fontWeight="800" fill="#64748B">PRECIO ACTUAL</text>
      <text x="28" y="80" fontSize="18" fontWeight="800" fill="#1A1A1A">$145.00</text>
      <text x="28" y="92" fontSize="7" fill="#64748B">Queso mozzarella · kg</text>

      <rect x="167" y="34" width="135" height="64" rx="10" fill="#FEF2F2" stroke="#FCA5A5"/>
      <text x="177" y="50" fontSize="7" fontWeight="800" fill="#991B1B">PROPUESTO</text>
      <text x="177" y="80" fontSize="18" fontWeight="800" fill="#DC2626">$156.60</text>
      <text x="177" y="92" fontSize="7" fill="#DC2626" fontWeight="700">+8.00% · +$11.60</text>

      <rect x="18" y="108" width="284" height="22" rx="6" fill="#FFFBE6" stroke="#FFDF00"/>
      <text x="28" y="122" fontSize="7.5" fill="#1A1A1A">Compras y Dirección deben aprobar antes que el cambio quede vigente</text>

      <rect x="18" y="138" width="138" height="28" rx="6" fill="#fff" stroke="#E2E8F0"/>
      <text x="36" y="156" fontSize="8" fontWeight="700" fill="#64748B">Compras: pendiente</text>
      <rect x="166" y="138" width="138" height="28" rx="6" fill="#fff" stroke="#E2E8F0"/>
      <text x="184" y="156" fontSize="8" fontWeight="700" fill="#64748B">Dirección: pendiente</text>
    </svg>
  ),
  Inventory: () => (
    <svg viewBox="0 0 320 180" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="320" height="180" rx="14" fill="#FFFBE6"/>
      <text x="20" y="22" fontSize="10" fontWeight="800" fill="#1A1A1A">Existencia en tu sucursal · sync POS</text>
      {[
        { name: "Queso mozzarella", q: "48 kg", low: false },
        { name: "Pepperoni", q: "4 kg ⚠", low: true },
        { name: "Cajas pizza 14\"", q: "1,250 pz", low: false },
        { name: "Coca-Cola 600 ml", q: "12 pz ⚠", low: true },
      ].map((it, i) => (
        <g key={i} transform={`translate(0, ${34 + i*30})`}>
          <rect x="14" y="0" width="292" height="24" rx="6" fill={it.low ? "#FEF2F2" : "#fff"} stroke={it.low ? "#FECACA" : "#E2E8F0"}/>
          <rect x="22" y="6" width="12" height="12" rx="2" fill={it.low ? "#EF4444" : "#FFDF00"}/>
          <text x="44" y="16" fontSize="9" fontWeight="700" fill="#1A1A1A">{it.name}</text>
          <text x="240" y="16" fontSize="9" fontWeight="700" fill={it.low ? "#DC2626" : "#1A1A1A"}>{it.q}</text>
        </g>
      ))}
    </svg>
  ),
};

// Visual mapping per role+step (icon and illustration only — text comes from i18n)
const VISUAL_BY_ROLE = {
  corporate_admin: [
    { icon: Pizza, illu: "Dashboard" },
    { icon: ShoppingCart, illu: "ApprovalFlow" },
    { icon: Truck, illu: "Receptions" },
    { icon: Tag, illu: "PriceChanges" },
    { icon: Bell, illu: "Alerts" },
  ],
  branch_manager: [
    { icon: Pizza, illu: "Dashboard" },
    { icon: Plus, illu: "CreatePO" },
    { icon: Truck, illu: "Receptions" },
    { icon: Bell, illu: "Inventory" },
  ],
  treasury: [
    { icon: Pizza, illu: "Dashboard" },
    { icon: CreditCard, illu: "Payments" },
    { icon: AlertTriangle, illu: "Alerts" },
    { icon: FileDown, illu: "Payments" },
  ],
  supplier: [
    { icon: Pizza, illu: "Dashboard" },
    { icon: ShoppingCart, illu: "ApprovalFlow" },
    { icon: Upload, illu: "Invoice" },
    { icon: Tag, illu: "PriceChanges" },
    { icon: Receipt, illu: "Receipts" },
  ],
  operativo: [
    { icon: Pizza, illu: "Dashboard" },
    { icon: ClipboardList, illu: "Receptions" },
    { icon: SprayCan, illu: "Inventory" },
    { icon: BarChart3, illu: "Reports" },
  ],
};

export default function OnboardingModal() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    // Suppliers use the dedicated welcome tour (/bienvenida-proveedor); don't auto-show the generic onboarding for them.
    if (user.role === "supplier") return;
    const dismissed = localStorage.getItem(STORAGE_PREFIX + user.role);
    if (!dismissed) {
      const tm = setTimeout(() => setOpen(true), 700);
      return () => clearTimeout(tm);
    }
  }, [user]);

  useEffect(() => {
    const reopen = () => { setStep(0); setOpen(true); };
    window.addEventListener("chizy:reopen-onboarding", reopen);
    return () => window.removeEventListener("chizy:reopen-onboarding", reopen);
  }, []);

  if (!user || !open) return null;

  const visuals = VISUAL_BY_ROLE[user.role] || VISUAL_BY_ROLE.corporate_admin;
  const roleSteps = t(`onboarding.${user.role}`, { returnObjects: true });
  const stepsCount = Array.isArray(roleSteps) ? roleSteps.length : visuals.length;
  const current = Array.isArray(roleSteps) ? roleSteps[step] : { title: "", lede: "", bullets: [], tip: "" };
  const visual = visuals[step] || visuals[0];
  const isLast = step === stepsCount - 1;
  const isFirst = step === 0;
  const Icon = visual.icon;
  const Illustration = Illu[visual.illu] || Illu.Dashboard;

  const close = () => {
    localStorage.setItem(STORAGE_PREFIX + user.role, "1");
    setOpen(false);
  };
  const next = () => isLast ? close() : setStep(step + 1);
  const prev = () => !isFirst && setStep(step - 1);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex items-center justify-center p-4"
        data-testid="onboarding-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ type: "spring", damping: 22, stiffness: 300 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[92vh]"
        >
          <div className="relative bg-gradient-to-br from-[#1A1A1A] to-[#2D2D2D] text-white p-7 sm:p-9">
            <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: "radial-gradient(circle at 25% 30%, #FFDF00 0%, transparent 30%), radial-gradient(circle at 75% 70%, #FFDF00 0%, transparent 25%)",
            }} />
            <button
              data-testid="onboarding-close-x"
              onClick={close}
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-md transition-colors"
              aria-label={t("onboarding.closeAria")}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative flex items-center gap-3 mb-4">
              <motion.div
                key={step}
                initial={{ scale: 0.6, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", damping: 12 }}
                className="w-12 h-12 rounded-2xl bg-[#FFDF00] flex items-center justify-center shadow-lg shadow-[#FFDF00]/30"
              >
                <Icon className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2.2} />
              </motion.div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFDF00] font-bold">
                  {t("onboarding.stepLabel", { step: step + 1, total: stepsCount })}
                </div>
                <h2
                  data-testid="onboarding-step-title"
                  className="font-display font-extrabold text-xl sm:text-2xl leading-tight tracking-tight mt-0.5"
                >
                  {current.title}
                </h2>
              </div>
            </div>
            <p className="relative text-slate-300 text-sm leading-relaxed">
              {current.lede}
            </p>

            <div className="relative mt-4 flex items-center gap-1.5">
              {Array.from({ length: stepsCount }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step ? "bg-[#FFDF00] w-8" : i < step ? "bg-[#FFDF00]/40 w-4" : "bg-white/20 w-4"
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-slate-50">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
                className="grid md:grid-cols-2 gap-0"
              >
                <div className="bg-gradient-to-br from-[#FFFBE6] to-white p-5 sm:p-7 flex items-center justify-center border-b md:border-b-0 md:border-r border-slate-200">
                  <div
                    className="w-full max-w-sm aspect-[16/9] rounded-2xl overflow-hidden shadow-md border border-slate-200"
                    data-testid={`onboarding-illustration-${step + 1}`}
                    aria-label={current.title}
                    role="img"
                  >
                    <Illustration />
                  </div>
                </div>
                <div className="p-5 sm:p-7">
                  <ul className="space-y-2.5">
                    {(current.bullets || []).map((b, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.06 }}
                        className="flex items-start gap-2.5"
                      >
                        <div className="w-5 h-5 rounded-full bg-[#FFDF00] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-[#1A1A1A]" strokeWidth={3} />
                        </div>
                        <span className="text-sm text-slate-700 leading-relaxed">{b}</span>
                      </motion.li>
                    ))}
                  </ul>

                  <div className="mt-5 bg-[#FFFBE6] border border-[#FFDF00] rounded-xl p-3.5">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#1A1A1A] mb-1">
                      {t("onboarding.tipFast")}
                    </div>
                    <p className="text-xs text-slate-700 leading-relaxed">{current.tip}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="border-t border-slate-200 bg-white px-6 py-4 sm:px-8 sm:py-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              {isLast ? (
                <div className="text-[11px] text-slate-500">
                  <Trans i18nKey="onboarding.reopenHint" components={{ strong: <span className="font-semibold" /> }} />
                </div>
              ) : (
                <button
                  data-testid="onboarding-skip"
                  onClick={close}
                  className="text-xs text-slate-500 hover:text-[#1A1A1A] font-semibold"
                >
                  {t("onboarding.skip")}
                </button>
              )}

              <div className="flex items-center gap-2">
                {!isFirst && (
                  <button
                    data-testid="onboarding-prev"
                    onClick={prev}
                    className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 flex items-center gap-1.5"
                  >
                    <ChevronLeft className="w-4 h-4" /> {t("onboarding.prev")}
                  </button>
                )}
                <button
                  data-testid="onboarding-next"
                  onClick={next}
                  className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-1.5"
                >
                  {isLast ? t("onboarding.start") : t("onboarding.next")}
                  {!isLast && <ChevronRight className="w-4 h-4" strokeWidth={2.5} />}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
